/* eslint-disable react-hooks/exhaustive-deps */
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { ChevronRight } from '@mui/icons-material'
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItem as MuiListItem,
  ListItemIcon as MuiListItemIcon,
  Typography,
} from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import i18n from 'i18n'

import { RootState } from 'redux/store'
import { setActiveNavItemIndex, toggleNavigation } from 'redux/ui/common/themeUI'

import AnchorButton from 'components/atoms/buttons/AnchorButton'
import SearchBar from 'components/molecules/SearchBar'

import { useMedia } from 'utils/customHooks/useMedia'
import { DRAWER_WIDTH, ELEMENT_RADIUS_MID, HEADER_HEIGHT } from 'utils/customizedTheme'
import { INavItem, navigationModel } from 'utils/navigationModel'

import Logo from 'assets/svg/logo.svg'

import { RoleType } from 'types'

const Navigation = () => {
  const { isUnder900px } = useMedia()
  const theme = useTheme()
  const md = useMediaQuery(theme.breakpoints.up('md'))
  const xl = useMediaQuery(theme.breakpoints.up('xl'))


  const userData = useSelector((state: RootState) => state['user'].user)
  const userRoleType = useMemo(() => userData?.role, [userData])

  const [navItems, setNavItems] = useState([])

  const openNavigation = useSelector((state: RootState) => state.theme.openNavigation)

  useEffect(() => {
    if (userRoleType) {
      const newNavItems = navigationModel(userRoleType) //useMemo
      setNavItems(newNavItems)
    }
  }, [userRoleType])

  const dispatch = useDispatch()

  function hasPermissionRecursive(item: INavItem, roleType: RoleType): boolean {
    let permission = false
    if (item.children) {
      for (let i = 0; i < item.children.length; i++) {
        const child = item.children[i]
        if (child.children) {
          permission = hasPermissionRecursive(child, roleType)
          if (permission) break
        } else {
          permission = child.roles.includes(roleType)
          if (permission) break
        }
      }
    } else {
      permission = item.roles.includes(roleType)
    }
    return permission
  }
  const hasPermission = useCallback(
    (item: INavItem, userRoleType: RoleType) => hasPermissionRecursive(item, userRoleType),
    [userRoleType],
  )

  return (
    <Drawer
      sx={{
        width: DRAWER_WIDTH,
        zIndex: theme.zIndex.appBar - 1,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
        },
      }}
      variant={xl ? 'persistent' : null}
      hideBackdrop={false}
      anchor='left'
      open={openNavigation}
      onClose={() => dispatch(toggleNavigation())}
    >
      <DrawerHeader />
      {/* <Divider /> */}

      {isUnder900px && !['parent','student'].includes(userData?.role) && (
        <List sx={{ padding: '10px', color: `${theme.palette.primary.light}` }}>
          <SearchBar border={false} />
        </List>
      )}
      <List sx={{ padding: '10px', color: `${theme.palette.primary.light}` }}>
        {navItems.map((item, index) =>
          hasPermission(item, userRoleType) && !item.skip ? (
            <NavItemRecursive
              key={index}
              item={item}
              indentation={0.8}
              md={md}
              hasPermission={hasPermission}
              userRoleType={userRoleType}
              index={index}
            />
          ) : null,
        )}
      </List>
      <DrawerFooter />
    </Drawer>
  )
}

export default memo(Navigation)

interface INavProps {
  item: INavItem
  indentation: number
  md: boolean
  hasPermission: any
  userRoleType: RoleType
  index: number
}

const NavItemRecursive: FC<INavProps> = ({ item, indentation, md, hasPermission, userRoleType, index }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()

  const activeItemIndex = useSelector((state: RootState) => state.theme.activeNavItemIndex)

  const isItemOpenRecursive = (children: INavItem[]) => {
    for (let i = 0; i < children.length; i++) {
      const child = children[i]
      if (child.children) {
        return isItemOpenRecursive(child.children)
      } else {
        if (`/${child.route}` === location.pathname) return true
      }
    }
  }

  const activeItemIndexOnReload = useMemo(
    () => (item.children ? isItemOpenRecursive(item.children) : false),
    [],
  )
  const isActive = (activeItemIndex === null && activeItemIndexOnReload) || index === activeItemIndex

  return (
    <>
      <ListItem
        onClick={(e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
          if (item.route) {
            navigate(item.route)
          } else {
            dispatch(setActiveNavItemIndex(index === activeItemIndex ? -1 : index))
          }

          if (!md && !item.children) {
            dispatch(toggleNavigation())
          }
        }}
        selected={`/${item.route}` === location.pathname || location.pathname.includes(`/${item.route}/`)}
        disablePadding
      >
        <ListItemButton
          sx={{ paddingLeft: `${indentation}rem`, paddingTop: '0.6rem', paddingBottom: '0.6rem' }}
        >
          <ListItemIcon sx={{ minWidth: 'auto', marginRight: '0.5rem', color: 'rgba(0,0,0,0.5)' }}>{item.icon}</ListItemIcon>
          <ListItemText>
            <Typography sx={{ fontWeight: 500, lineHeight: '1.2' }}>{item.label}</Typography>
          </ListItemText>
          {item.children && (
            <ChevronRight
              sx={{ transition: 'all 0.2s ease', transform: `rotate(${isActive ? 90 : 0}deg)` }}
            />
          )}
        </ListItemButton>
      </ListItem>
      {item.children && (
        <Collapse in={isActive} timeout='auto' unmountOnExit>
          {item.children.map((child, index) =>
            hasPermission(child, userRoleType) && !child.skip ? (
              <NavItemRecursive
                key={index}
                item={child}
                indentation={indentation + 0.8}
                md={md}
                hasPermission={hasPermission}
                userRoleType={userRoleType}
                index={index}
              />
            ) : null,
          )}
        </Collapse>
      )}
    </>
  )
}

const ListItem = styled(MuiListItem)(({ theme }) => ({
  '&': {
    color: theme.palette.white.contrastText,
    borderRadius: ELEMENT_RADIUS_MID,
  },
  '& .MuiButtonBase-root': {
    borderRadius: ELEMENT_RADIUS_MID,
  },
  '&.Mui-selected': {
    color: theme.palette.white.contrastText,
    backgroundColor: theme.palette.primary.main,
  },
}))

const ListItemIcon = styled(MuiListItemIcon)(({ theme }) => ({
  color: 'inherit',
}))

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  height: HEADER_HEIGHT,
  ...theme.mixins.toolbar, // content to be below app bar
  justifyContent: 'flex-end',
}))

const DrawerFooter = () => {
  const theme = useTheme()
  return (
    <AnchorButton
      to='about-us'
      text={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            fontWeight: 500,
            height: '2.75rem',
            color: theme.palette.primary.main,
            background: 'inherit',
            '&:hover': {
              color: '#fff',
              background: theme.palette.primary.main,
            },
          }}
        >
          <Box
            component='img'
            sx={{
              height: '1.5rem',
              width: 'auto',
              marginLeft: '0.8rem',
              marginRight: '0.5rem',
            }}
            alt='Esybee'
            src={Logo}
          />
          {`© v.${process.env.REACT_APP_VERSION}`}
        </Box>
      }
      sx={{
        marginTop: 'auto',
      }}
    />
  )
}
